/**
 * Выбор кнопки шаблона как триггера автоответа
 * Дата: 07.02.2026 - Создан для TODO-053 (Template Buttons ↔ Auto-Replies)
 * Дата: 28.02.2026 - FIX: корректное отображение WABA шаблона-источника в режиме редактирования,
 * когда в правиле сохранён local UUID из message_templates, а UI загружает источники по templateGuid из Wazzup.
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/pages/AutoReplies.tsx - используется в форме создания/редактирования правил автоответов
 * - frontend/src/api/templates.ts - Template, WABATemplate интерфейсы
 * - frontend/src/api/auto-replies.ts - AutoReplyRule интерфейс
 * 
 * КАК РАБОТАЕТ:
 * 1. Dropdown: «Выберите шаблон-источник» → загрузить шаблоны с кнопками
 * 2. Для WABA: загрузить WABA шаблоны из API, показать кнопки
 * 3. Для TG: загрузить наши шаблоны с buttons != [], показать кнопки
 * 4. Чекбоксы: выбрать на какие кнопки реагировать (можно несколько)
 * 5. Сохранить: waba_trigger_template_id + waba_button_ids[] или tg_trigger_template_id + tg_callback_data[]
 * 
 * СХЕМА:
 * AutoReplies.tsx → ButtonTriggerSelector → выбор кнопок → formData.waba_button_ids / tg_callback_data
 */
import { useState, useEffect } from 'react';
import { Select, Badge } from '@/components/ui';
import { templatesApi, Template, WABATemplate } from '@/api/templates';
import { AutoReplyRule } from '@/api/auto-replies';
import { useToast } from '@/hooks/useToast';
import { getChannelDisplayName } from '@/config/channels';

interface ButtonTriggerSelectorProps {
  triggerType: 'waba_button' | 'tg_callback';
  formData: Partial<AutoReplyRule>;
  onChange: (data: Partial<AutoReplyRule>) => void;
  tenantId?: string;
  testId?: string;
}

export function ButtonTriggerSelector({ 
  triggerType, 
  formData, 
  onChange, 
  tenantId,
  testId 
}: ButtonTriggerSelectorProps) {
  const { showError } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [dbTemplates, setDbTemplates] = useState<Template[]>([]);
  const [wabaTemplates, setWabaTemplates] = useState<WABATemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedButtons, setSelectedButtons] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // Загрузка шаблонов при монтировании
  useEffect(() => {
    loadTemplates();
  }, [tenantId, triggerType]);

  // 28.02.2026 — FIX UX: при редактировании старых правил показываем выбранный источник корректно.
  // Инициализация из formData (с поддержкой legacy-значений waba_trigger_template_id)
  // В БД могли сохраниться:
  // - local template UUID (message_templates.id)
  // - external template guid (Wazzup templateGuid)
  useEffect(() => {
    if (triggerType === 'waba_button' && formData.waba_trigger_template_id) {
      const rawTemplateID = formData.waba_trigger_template_id;
      let resolvedTemplateID = rawTemplateID;

      const isWabaTemplateID = wabaTemplates.some((t) => t.id === rawTemplateID);
      if (!isWabaTemplateID) {
        const matchedDBTemplate = dbTemplates.find((t) => t.id === rawTemplateID);
        if (matchedDBTemplate?.external_template_id) {
          resolvedTemplateID = matchedDBTemplate.external_template_id;
        }
      }

      setSelectedTemplateId(resolvedTemplateID);
      setSelectedButtons(new Set(formData.waba_button_ids || []));
    } else if (triggerType === 'tg_callback' && formData.tg_trigger_template_id) {
      setSelectedTemplateId(formData.tg_trigger_template_id);
      setSelectedButtons(new Set(formData.tg_callback_data || []));
    }
  }, [
    triggerType,
    formData.waba_trigger_template_id,
    formData.waba_button_ids,
    formData.tg_trigger_template_id,
    formData.tg_callback_data,
    wabaTemplates,
    dbTemplates,
  ]);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      if (triggerType === 'waba_button') {
        // 28.02.2026 — подгружаем и Wazzup-источники, и локальные шаблоны БД для mаппинга ID.
        const [wabaData, dbData] = await Promise.all([
          templatesApi.getWABATemplates(tenantId),
          templatesApi.list(tenantId),
        ]);
        setWabaTemplates(wabaData.templates || []);
        setDbTemplates(dbData.templates || []);
      } else {
        const data = await templatesApi.list(tenantId);
        // 16.03.26 - Для inline callback автоответов поддерживаем источники как TG, так и MAX.
        const templatesWithButtons = (data.templates || []).filter(
          t =>
            !!t.buttons &&
            t.buttons.length > 0 &&
            ['telegram', 'telegram_bot', 'max'].includes((t.messenger || '').toLowerCase())
        );
        setTemplates(templatesWithButtons);
      }
    } catch (error: any) {
      showError('Ошибка загрузки шаблонов: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setSelectedButtons(new Set());
    
    if (triggerType === 'waba_button') {
      const template = wabaTemplates.find(t => t.id === templateId);
      if (template) {
        onChange({
          ...formData,
          waba_trigger_template_id: templateId,
          waba_button_ids: [],
        });
      }
    } else {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        onChange({
          ...formData,
          tg_trigger_template_id: templateId,
          tg_callback_data: [],
        });
      }
    }
  };

  const handleButtonToggle = (buttonId: string) => {
    const newSet = new Set(selectedButtons);
    if (newSet.has(buttonId)) {
      newSet.delete(buttonId);
    } else {
      newSet.add(buttonId);
    }
    setSelectedButtons(newSet);
    
    if (triggerType === 'waba_button') {
      onChange({
        ...formData,
        waba_button_ids: Array.from(newSet),
      });
    } else {
      onChange({
        ...formData,
        tg_callback_data: Array.from(newSet),
      });
    }
  };

  const getSelectedTemplate = () => {
    if (triggerType === 'waba_button') {
      return wabaTemplates.find(t => t.id === selectedTemplateId);
    } else {
      return templates.find(t => t.id === selectedTemplateId);
    }
  };

  const getButtons = () => {
    const template = getSelectedTemplate();
    if (!template) return [];
    
    if (triggerType === 'waba_button') {
      const wabaTemplate = template as WABATemplate;
      return (wabaTemplate.buttons || []).map((btn, idx) => ({
        id: btn.text || `btn_${idx}`, // WABA кнопки могут не иметь ID, используем text
        text: btn.text,
        type: btn.type,
      }));
    } else {
      const tgTemplate = template as Template;
      return (tgTemplate.buttons || []).map(btn => ({
        id: btn.callback_data,
        text: btn.text,
        type: btn.type,
      }));
    }
  };

  return (
    <div className="space-y-4" data-testid={testId || 'button-trigger-selector'}>
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Выберите шаблон-источник *
        </label>
        <Select
          value={selectedTemplateId}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          options={[
            { value: '', label: 'Выберите шаблон...' },
            ...(triggerType === 'waba_button'
              ? wabaTemplates.map(t => ({
                  value: t.id,
                  label: `${t.name}${t.title ? ` (${t.title})` : ''}`,
                }))
              : templates.map(t => ({
                  value: t.id,
                  label: `${t.name} [${getChannelDisplayName(t.messenger || '')}]`,
                }))),
          ]}
          testId="select-template-source"
          disabled={isLoading}
        />
        {isLoading && (
          <p className="text-xs text-neutral-500 mt-1">Загрузка шаблонов...</p>
        )}
        {!isLoading && selectedTemplateId && (
          <p className="text-xs text-neutral-500 mt-1">
            {triggerType === 'waba_button'
              ? 'Выберите кнопки WABA шаблона, на которые будет реагировать автоответ'
              : 'Выберите кнопки inline-шаблона, на которые будет реагировать автоответ'}
          </p>
        )}
      </div>

      {selectedTemplateId && getButtons().length > 0 && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Выберите кнопки *
          </label>
          <div className="space-y-2 border rounded-lg p-3 bg-neutral-50">
            {getButtons().map((btn) => (
              <label
                key={btn.id}
                className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer"
                data-testid={`checkbox-button-${btn.id}`}
              >
                <input
                  type="checkbox"
                  checked={selectedButtons.has(btn.id)}
                  onChange={() => handleButtonToggle(btn.id)}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">{btn.text}</span>
                <Badge variant="secondary" className="text-xs">
                  {btn.type}
                </Badge>
              </label>
            ))}
          </div>
          {selectedButtons.size === 0 && (
            <p className="text-xs text-orange-600 mt-1">
              ⚠️ Выберите хотя бы одну кнопку
            </p>
          )}
        </div>
      )}

      {selectedTemplateId && getButtons().length === 0 && (
        <div className="text-sm text-neutral-500 p-3 border border-dashed rounded-lg text-center">
          В выбранном шаблоне нет кнопок
        </div>
      )}
    </div>
  );
}
